import { useMutation, useQueryClient } from 'react-query';

import { ACTIVE_LICENSE_QUERY_KEY } from 'containers/Subscription/hooks';
import { getUserCurrentStore } from 'utils/authTyped';

import type { WizardCompletionDto } from '@onboarding/application/dtos';
import { getWizardUseCases } from '@onboarding/composition';

type ActiveLicenseCache = { wizardCompleted?: boolean } & Record<string, unknown>;

/**
 * Hook to mark the onboarding wizard as completed.
 *
 * Wraps the completeWizard use case in a React Query mutation so the
 * presentation layer can consume loading/error state idiomatically.
 *
 * On success it patches the cached active-license query so the
 * `useLicenseRedirect` watcher reads `wizardCompleted: true` immediately
 * (avoiding a redirect back to /onboarding while the 60s poll catches up),
 * and triggers a background refetch to reconcile with the backend.
 *
 * @example
 * const { mutateAsync, isLoading } = useCompleteWizard();
 * await mutateAsync();
 */
export const useCompleteWizard = () => {
  const queryClient = useQueryClient();
  const { completeWizard } = getWizardUseCases();
  const storeUuid = getUserCurrentStore()?.uuid;

  return useMutation<WizardCompletionDto, Error, void>(() => completeWizard.execute(), {
    onSuccess: () => {
      const queryKey = [ACTIVE_LICENSE_QUERY_KEY, storeUuid];

      queryClient.setQueryData<ActiveLicenseCache | null | undefined>(queryKey, (prev) =>
        prev ? { ...prev, wizardCompleted: true } : prev,
      );
      queryClient.invalidateQueries(queryKey);
    },
  });
};
